import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDashboard } from '../../api/billingApi';
import WalletMeter from './WalletMeter';
import SpendControls from './SpendControls';
import UsageExplorer from './UsageExplorer';
import MarginModel from './MarginModel';

/**
 * BillingDashboard — full billing dashboard page.
 *
 * Props:
 *   orgId   — string UUID (from auth context / org selector)
 *   isAdmin — boolean
 */
function BillingDashboard({ orgId, isAdmin = false }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboard(orgId);
      setDashboard(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" /> Billing Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 px-3 py-1.5 rounded-md transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => navigate('/billing/topup')}
              className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md transition-colors"
            >
              Top Up
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Top row: wallet meter + spend controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <WalletMeter dashboard={dashboard} loading={loading} />
          <SpendControls dashboard={dashboard} loading={loading} />
        </div>

        {/* Usage explorer — full width */}
        {orgId && (
          <div className="mb-4">
            <UsageExplorer orgId={orgId} />
          </div>
        )}

        {/* Margin model */}
        {orgId && (
          <MarginModel orgId={orgId} isAdmin={isAdmin} />
        )}
      </div>
    </div>
  );
}

export default BillingDashboard;
